import { useContext, useEffect } from 'react';
import { isNil, isNotNil } from '~/utilities/type-guards';
import type { OnboardingContextType } from '~/onboarding/hooks/useOnboardingContext/OnboardingContext';
import {
  NOOP_ONBOARDING_CONTEXT,
  OnboardingContext,
} from '~/onboarding/hooks/useOnboardingContext/OnboardingContext';
import { useLoadFeatureFlags } from '~/singlePageApp/hooks/useLoadFeatureFlags';
import { useInRouterContext } from 'react-router-dom';

export const useOnboardingContext = (props?: { forceContext?: boolean }): OnboardingContextType => {
  const { forceContext = false } = props ?? {};
  const isLegacyPage = !useInRouterContext();
  const context = useContext(OnboardingContext);
  const { flags } = useLoadFeatureFlags(['flag_use_onboarding']);

  useEffect(() => {
    return () => {
      if (isNil(context)) {
        return;
      }

      const currentExperience = context.getCurrentExperience();
      const currentFeatureId = context.getCurrentFeatureId();
      if (currentExperience?.hideOnUnmount && isNotNil(currentFeatureId)) {
        context.hide(currentFeatureId);
      }
    };
  }, [context]);

  // If forceContext is true, we skip these checks and force a return of the context.
  // This is useful when we need the context in Testing or Storybook environments.
  if (!forceContext && (!flags.flag_use_onboarding.enabled || isLegacyPage)) {
    return NOOP_ONBOARDING_CONTEXT;
  }

  if (isNil(context)) {
    throw new Error('Must use useOnboarding within OnboardingProvider');
  }

  return context;
};
